import { OperationLocation } from "src/networkRequest/operationsService";

export interface OperationFormData {
  title: string;
  agencyAssignedIdentifier: string;
  description: string;
  date: string;
  time: string;
  operationType: {
    planned: boolean;
    emergency: boolean;
  };
}

export interface ValidationOptions {
  includeLocations?: boolean;
}

/**
 * Validates operation form data
 * @param formData - The form data to validate
 * @param primaryLocation - Primary location data (optional, only validated if includeLocations is true)
 * @param options - Validation options
 * @returns Array of validation error messages
 */
export const validateOperationForm = (
  formData: OperationFormData,
  primaryLocation?: OperationLocation,
  options: ValidationOptions = {}
): string[] => {
  const errors: string[] = [];
  const { includeLocations = false } = options;

  // Validate required operation fields
  if (!formData.title.trim()) {
    errors.push('Title is required');
  }

  if (!formData.agencyAssignedIdentifier.trim()) {
    errors.push('Agency Assigned Identifier is required');
  }

  if (!formData.date) {
    errors.push('Date is required');
  }

  // Validate that at least one operation type is selected
  if (!formData.operationType.planned && !formData.operationType.emergency) {
    errors.push('At least one operation type (Planned or Emergency) must be selected');
  }

  // Validate location fields if requested
  if (includeLocations && primaryLocation) {
    const locationErrors = validateLocationData(primaryLocation);
    errors.push(...locationErrors);
  }

  return errors;
};

/**
 * Validates location data
 * @param locationData - The location data to validate
 * @param isPrimary - Whether this is a primary location (default: true)
 * @returns Array of validation error messages
 */
export const validateLocationData = (locationData: OperationLocation, isPrimary: boolean = true): string[] => {
  const errors: string[] = [];

  // Only validate name as required for primary locations
  if (isPrimary && !locationData.name.trim()) {
    errors.push('Location Name is required');
  }
  // Additional location names are optional - no validation needed
  return errors;
};

/**
 * Checks if the operation form is valid
 * @param formData - The form data to validate
 * @param primaryLocation - Primary location data (optional)
 * @param options - Validation options
 * @returns True if form is valid, false otherwise
 */

/**
 * Checks if location data is valid
 * @param locationData - The location data to validate
 * @param isPrimary - Whether this is a primary location (default: true)
 * @returns True if location is valid, false otherwise
 */
export const isLocationDataValid = (locationData: OperationLocation, isPrimary: boolean = true): boolean => {
  return validateLocationData(locationData, isPrimary).length === 0;
};

export const isOperationFormValid = (
  formData: OperationFormData,
  primaryLocation?: OperationLocation,
  options: ValidationOptions = {}
): boolean => {
  return validateOperationForm(formData, primaryLocation, options).length === 0;
};